import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { useTranslation } from 'react-i18next';
import { LoaderSmall } from '../LoaderSmall/LoaderSmall';
import { formatWithCustomGigaBillion } from '../../utils/fomatNumbers';
import { ECHARTS_TOOLTIP_STYLE, escapeHtml } from '../CustomDashboard/WidgetRenderer/echartsUtils';
import styles from './HorizontalBarChart.module.scss';

export interface HorizontalBarDatum {
  /** Full category name; long names are truncated on the axis, not in the data. */
  name: string;
  value: number;
  /** Per-bar color override; defaults to the primary bar color. */
  color?: string;
}

interface HorizontalBarChartProps {
  chartData: HorizontalBarDatum[] | null;
  isClickable?: boolean;
  /** Receives the index of the clicked bar within `chartData`. */
  onSelect?: (selected: number | null) => void;
  height?: number;
  seriesName?: string;
}

// echarts SVG text cannot read CSS variables, so the palette is mirrored here.
export const BAR_COLOR = '#2463EB';
export const OTHER_BAR_COLOR = '#9AA5B4';
const AXIS_TEXT_COLOR = '#4f546c'; // --clr-gray500
const VALUE_TEXT_COLOR = '#151419'; // --main-color
const GRID_LINE_COLOR = '#f0f0f0';

const getBarWidth = (count: number): string => {
  if (count === 1) return '15%';
  if (count <= 5) return '25%';
  if (count <= 10) return '40%';
  return '50%';
};

export const HorizontalBarChart = ({
  chartData,
  isClickable = true,
  onSelect = () => {},
  height = 400,
  seriesName,
}: HorizontalBarChartProps) => {
  const { t } = useTranslation();
  const items = useMemo(() => chartData ?? [], [chartData]);

  const option = useMemo<EChartsOption>(
    () => ({
      aria: { enabled: true },
      grid: { left: 16, right: 64, top: 8, bottom: 8, containLabel: true },
      tooltip: {
        trigger: 'item',
        appendToBody: true,
        ...ECHARTS_TOOLTIP_STYLE,
        formatter: (params: any) => {
          const item = items[params.dataIndex];
          if (!item) return '';
          return `${params.marker}${escapeHtml(item.name)}: <b>${escapeHtml(
            formatWithCustomGigaBillion(item.value),
          )}</b>`;
        },
      },
      xAxis: {
        type: 'value',
        min: 0,
        axisLabel: { fontSize: 14, color: '#666', formatter: (val: number) => formatWithCustomGigaBillion(val) },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: GRID_LINE_COLOR } },
      },
      yAxis: {
        type: 'category',
        // Keep the data order top-to-bottom, as the previous chart did.
        inverse: true,
        data: items.map((item) => item.name),
        axisLabel: { fontSize: 14, color: AXIS_TEXT_COLOR, width: 100, overflow: 'truncate' },
        axisLine: { lineStyle: { color: '#ccc' } },
        axisTick: { show: false },
      },
      series: [
        {
          type: 'bar',
          name: seriesName,
          barWidth: getBarWidth(items.length),
          cursor: isClickable ? 'pointer' : 'default',
          data: items.map((item) => {
            const color = item.color ?? BAR_COLOR;
            return {
              value: item.value,
              itemStyle: { color },
              // A same-colored stroke is drawn centered on the edge, so it grows
              // the hovered bar by 2px on every side (bars have no `scale`).
              emphasis: { itemStyle: { color, borderColor: color, borderWidth: 4 } },
            };
          }),
          label: {
            show: true,
            position: 'right',
            distance: 8,
            fontSize: 12,
            fontWeight: 'bold',
            color: VALUE_TEXT_COLOR,
            formatter: (params: any) => String(params.value),
          },
          // No `focus` blur: dimming the other bars on every hover flickers as
          // the pointer crosses bar gaps. Highlight only the hovered bar instead.
          emphasis: {
            itemStyle: { shadowBlur: 6, shadowOffsetY: 1, shadowColor: 'rgba(21, 25, 25, 0.25)' },
          },
        },
      ],
    }),
    [items, isClickable, seriesName],
  );

  const onEvents = useMemo(() => {
    if (!isClickable) return undefined;
    return {
      click: (params: any) => {
        const index = typeof params?.dataIndex === 'number' ? params.dataIndex : null;
        onSelect(index);
      },
    };
  }, [isClickable, onSelect]);

  if (chartData === null) {
    return (
      <div className={styles.container}>
        <div className={styles.loaderWrapper}>
          <LoaderSmall />
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.noData}>{t('There is no data to display')}</div>
      </div>
    );
  }

  return (
    <div className={styles.container} translate="no">
      <ReactECharts
        option={option}
        notMerge
        style={{ width: '100%', height: `${height}px` }}
        opts={{ renderer: 'svg' }}
        onEvents={onEvents}
      />
    </div>
  );
};
